// PB-5294. The two PUBLIC set-password endpoints.
//
// A separate module from `invites-api.ts` for the same reason
// `invite-redemption-api.ts` is: that one is the ADMIN surface, running behind
// the auth guard, sending the CSRF header, with errors shaped for somebody who
// is already signed in. These calls are made by a visitor with no session and
// no account, so they share none of it — and sharing a module would invite a
// future edit to give them the admin client's redirect-on-401, which for this
// visitor means being bounced to a login they cannot complete.
//
// THE TOKEN TRAVELS IN THE REQUEST BODY, and it reaches the browser in the URL
// FRAGMENT rather than the path. A fragment is never transmitted in an HTTP
// request and is stripped from `Referer`, so it cannot reach nginx, a proxy, or
// a downstream log. That is a deliberate divergence from `/invite/<token>`:
// `apps/shell/nginx.conf.template` declares no `log_format`, so the image
// default `combined` applies and logs `$http_referer`; `Referrer-Policy` is
// `strict-origin-when-cross-origin`, which sends the full URL same-origin; and
// `location ^~ /assets/` has no `access_log off`. Every first load of an invite
// link therefore writes its raw token to pod stdout today, defeating the
// `access_log off` on `/invite/` that exists to prevent exactly that.

/** What the preview may disclose, and it is deliberately almost nothing. */
export interface SetPasswordPreview {
  /** Tenant display name, so the page is not baffling. */
  readonly workspaceName: string;
  /** The address this account belongs to, so the person can confirm it. */
  readonly email: string | null;
  readonly expiresAt: string;
}

/**
 * Every refusal, without saying which one it was.
 *
 * The server returns one identical 404 for an unknown token, a consumed one,
 * one superseded by an administrator's re-send, an expired one, and one
 * belonging to another workspace. Kept collapsed here rather than made
 * helpful: anything that distinguished them would rebuild, in the browser, the
 * oracle the server was careful not to give.
 */
export class SetPasswordNotUsableError extends Error {
  constructor() {
    super("This set-password link is not valid.");
    this.name = "SetPasswordNotUsableError";
  }
}

/**
 * The workspace's own password policy refused this value.
 *
 * Carries Keycloak's wording verbatim. The realm is the authority on what it
 * refused (AC-5) and a second wording here would be a second thing to drift —
 * and would be wrong the moment an administrator changes the policy.
 */
export class PasswordRejectedError extends Error {
  constructor(reason: string) {
    super(reason);
    this.name = "PasswordRejectedError";
  }
}

/** The request was refused for a reason that is NOT about the token. */
/**
 * The credential write reached Keycloak and its outcome is UNKNOWN — a timeout,
 * a dropped connection, or a 5xx.
 *
 * Distinct from `SetPasswordRequestFailedError` because the honest thing to say
 * is different, and saying the wrong one is a real harm. A request that never
 * reached Keycloak changed nothing, so "your password was not changed" is true
 * and the visitor should retry. This one may have SET the password — auth
 * cannot tell, which is exactly why it parks the token and spends the link — so
 * telling them nothing happened would send them to an administrator for a new
 * link when the password they just chose may already work.
 */
export class SetPasswordOutcomeUnknownError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SetPasswordOutcomeUnknownError";
  }
}

export class SetPasswordRequestFailedError extends Error {
  constructor(readonly status: number) {
    super("The sign-in service could not be reached.");
    this.name = "SetPasswordRequestFailedError";
  }
}

/**
 * The server's single refusal, and the ONLY 404 that means "this link is dead".
 *
 * A bare `status === 404` is not enough. A 404 also arrives when the ROUTE is
 * absent — auth deployed behind the shell, a gateway that has not learned the
 * path, a proxy on the wrong upstream — and Argo makes that a real rollout
 * state: the shell and auth are separate applications, so there is a window in
 * which this page exists and the endpoint does not. Without this check every
 * invitee who clicks during that window is told, permanently and with no
 * retry, that a perfectly good link is spent. The invitation route shipped
 * that bug once; this is the same fix.
 */
async function isNotUsable(response: Response): Promise<boolean> {
  if (response.status !== 404) return false;
  try {
    const body: unknown = await response.clone().json();
    return (
      typeof body === "object" &&
      body !== null &&
      (body as { code?: unknown }).code === "set_password_not_usable"
    );
  } catch {
    return false;
  }
}

async function readRejection(response: Response): Promise<string | null> {
  if (response.status !== 422) return null;
  try {
    const body: unknown = await response.clone().json();
    const message =
      typeof body === "object" && body !== null
        ? (body as { message?: unknown }).message
        : null;
    return typeof message === "string" && message.length > 0 ? message : null;
  } catch {
    return null;
  }
}

async function post(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: "POST",
    /* No `credentials: "include"`. This visitor has no session, and sending
       one if a stale cookie happens to exist would make the request behave
       differently for a signed-in visitor than for the anonymous one this
       page is built for. */
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(body),
  });
}

/**
 * Read-only. Safe for a mail scanner to fetch, which matters: Defender Safe
 * Links, Proofpoint and Mimecast all dereference URLs in inbound mail before
 * the human clicks, so anything that consumed the token here would be spent by
 * the scanner and the real invitee would always find a dead link.
 *
 * A fragment is not sent to the server at all, so a scanner following the
 * emailed URL never even reaches this call — but the page must still be safe
 * for one that executes JavaScript.
 */
export async function previewSetPassword(
  setPasswordToken: string,
): Promise<SetPasswordPreview> {
  const response = await post("/api/auth/set-password/preview", {
    setPasswordToken,
  });
  if (await isNotUsable(response)) throw new SetPasswordNotUsableError();
  if (!response.ok) throw new SetPasswordRequestFailedError(response.status);
  return (await response.json()) as SetPasswordPreview;
}

/**
 * Set the password. Resolves on success; no session is minted, so the person
 * then signs in the ordinary way.
 *
 * Both names are required by the server, and not as a formality: the OIDC
 * callback refuses a local profile missing either, so an account without them
 * cannot be signed into at all. This page is the last place they can be
 * collected.
 */
export async function completeSetPassword(input: {
  readonly setPasswordToken: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly password: string;
}): Promise<void> {
  const response = await post("/api/auth/set-password/complete", input);
  if (await isNotUsable(response)) throw new SetPasswordNotUsableError();
  const rejection = await readRejection(response);
  if (rejection !== null) throw new PasswordRejectedError(rejection);
  const unknown = await readOutcomeUnknown(response);
  if (unknown !== null) throw new SetPasswordOutcomeUnknownError(unknown);
  if (!response.ok) throw new SetPasswordRequestFailedError(response.status);
}

/**
 * The server's own sentence for `keycloak_unavailable`, or null.
 *
 * Matched on the CODE and not the 503 status: auth returns 503 for several
 * dependencies, and only this one means the credential write's outcome is
 * genuinely unknown. Anything else 503 is an ordinary non-answer and keeps the
 * generic retry copy.
 */
async function readOutcomeUnknown(response: Response): Promise<string | null> {
  if (response.status !== 503) return null;
  try {
    const body: unknown = await response.clone().json();
    if (
      typeof body !== "object" ||
      body === null ||
      (body as { code?: unknown }).code !== "keycloak_unavailable"
    ) {
      return null;
    }
    const message = (body as { message?: unknown }).message;
    return typeof message === "string" && message.length > 0 ? message : null;
  } catch {
    return null;
  }
}
